import { BAGGAGE_STATUS } from "../types";
import { resolveFlightBaggageDisplayState } from "../../../../Components/FlightBookingFlowMockup/adapters/flightBaggageStepPolicy";

/**
 * Map offer baggage → BaggageViewModel[].
 * Absence of included + absence of purchase options ⇒ unknown (never notIncluded).
 * notIncluded only when explicitNotIncludedSignal === true.
 *
 * LDS included/paid semantics: reuse resolveFlightBaggageDisplayState(rawDeal)
 * (same helper as V1 Results card) — no new string parsing.
 *
 * @param {object|null|undefined} offer — adapted FE offer (may include rawDeal for mapping only)
 * @param {{ explicitNotIncludedSignal?: boolean, selections?: object|null }} [ctx]
 * @returns {import("../types").BaggageViewModel[]}
 */
export function mapBaggageViewModels(offer, ctx = {}) {
  const out = [];
  const bag = offer?.baggage && typeof offer.baggage === "object" ? offer.baggage : null;

  const carryOn = bag ? normalizeQty(bag.carryOn) : null;
  const checked = bag ? normalizeQty(bag.checked) : null;

  if (carryOn != null && carryOn > 0) {
    out.push(includedRow("carryOn", carryOn));
  }
  if (checked != null && checked > 0) {
    out.push(includedRow("checked", checked));
  }

  // Same SSOT as V1 FlightOfferResultCardLayout / getFlightBaggageIncludedBadge
  const resolved = offer?.rawDeal
    ? resolveFlightBaggageDisplayState(offer.rawDeal)
    : null;
  if (resolved?.displayState === "included") {
    const hasCarry =
      resolved.hasTrolley ||
      resolved.hasCarryOn ||
      (resolved.includedLines || []).some((l) => /טרולי|כבודת יד/u.test(l));
    const hasChecked =
      resolved.hasCheckedBag ||
      (resolved.includedLines || []).some((l) => /מזוודה/u.test(l));
    if (
      hasCarry &&
      !out.some((r) => r.kind === "carryOn" && r.status === BAGGAGE_STATUS.INCLUDED)
    ) {
      out.push(
        includedRow(
          "carryOn",
          1,
          (resolved.includedLines || []).find((l) => /טרולי|כבודת יד/u.test(l)) ||
            null
        )
      );
    }
    if (
      hasChecked &&
      !out.some((r) => r.kind === "checked" && r.status === BAGGAGE_STATUS.INCLUDED)
    ) {
      out.push(
        includedRow(
          "checked",
          1,
          (resolved.includedLines || []).find((l) => /מזוודה/u.test(l)) || null
        )
      );
    }
    if (
      out.filter((r) => r.status === BAGGAGE_STATUS.INCLUDED).length === 0 &&
      (resolved.includedLines || []).length > 0
    ) {
      out.push({
        status: BAGGAGE_STATUS.INCLUDED,
        kind: "unknown",
        quantity: null,
        weightKg: null,
        currency: null,
        price: null,
        passengerId: null,
        segmentIndex: null,
        label: resolved.includedLines.join(" · "),
      });
    }
  } else if (resolved?.displayState === "paid_upgrade") {
    if (!out.some((r) => r.status === BAGGAGE_STATUS.AVAILABLE_FOR_PURCHASE)) {
      out.push({
        status: BAGGAGE_STATUS.AVAILABLE_FOR_PURCHASE,
        kind: "unknown",
        quantity: resolved.paidUpgradeOptionsCount || null,
        weightKg: null,
        currency: null,
        price: null,
        passengerId: null,
        segmentIndex: null,
        label: null,
      });
    }
  }
  // displayState === "no_baggage" → leave for unknown (never invent notIncluded)

  if (
    ctx.explicitNotIncludedSignal === true &&
    carryOn === 0 &&
    checked === 0
  ) {
    out.push({
      status: BAGGAGE_STATUS.NOT_INCLUDED,
      kind: "checked",
      quantity: 0,
      weightKg: null,
      currency: null,
      price: null,
      passengerId: null,
      segmentIndex: null,
      label: null,
    });
  }

  const purchaseRows = collectPurchaseOptions(offer);
  for (const row of purchaseRows) {
    out.push(row);
  }

  const selected = mapSelectedPaid(ctx.selections, offer);
  for (const row of selected) {
    out.push(row);
  }

  if (out.length === 0) {
    return [
      {
        status: BAGGAGE_STATUS.UNKNOWN,
        kind: null,
        quantity: null,
        weightKg: null,
        currency: null,
        price: null,
        passengerId: null,
        segmentIndex: null,
        label: null,
      },
    ];
  }
  return out;
}

function includedRow(kind, quantity, label = null) {
  return {
    status: BAGGAGE_STATUS.INCLUDED,
    kind,
    quantity,
    weightKg: null,
    currency: null,
    price: null,
    passengerId: null,
    segmentIndex: null,
    label,
  };
}

function normalizeQty(v) {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function collectPurchaseOptions(offer) {
  const rows = [];
  const extras = Array.isArray(offer?.availableExtras) ? offer.availableExtras : [];
  for (const e of extras) {
    const type = String(e?.type || "").toLowerCase();
    if (type && type !== "baggage") continue;
    if (!type && !looksLikeBaggageExtra(e)) continue;
    const amount = finiteOrNull(e.totalAmount ?? e.amount ?? e.price);
    rows.push({
      status: BAGGAGE_STATUS.AVAILABLE_FOR_PURCHASE,
      kind: mapBaggageKind(e.metadata?.baggageType || e.baggageType),
      quantity: finiteOrNull(e.quantity) ?? 1,
      weightKg: finiteOrNull(e.metadata?.maximumWeightKg ?? e.maximumWeightKg),
      currency: strOrNull(e.totalCurrency ?? e.currency),
      price: amount,
      passengerId: Array.isArray(e.passengerIds) ? e.passengerIds[0] ?? null : null,
      segmentIndex: Array.isArray(e.sliceIndices) ? e.sliceIndices[0] ?? null : null,
      label: strOrNull(e.name || e.label),
    });
  }
  return rows;
}

function looksLikeBaggageExtra(e) {
  const t = String(e?.metadata?.baggageType || e?.baggageType || "").toLowerCase();
  return Boolean(t) || e?.serviceId != null;
}

function mapSelectedPaid(selections, offer) {
  if (!selections || typeof selections !== "object") return [];
  const rows = [];

  const duffel = selections.duffelServiceSelections;
  if (duffel && typeof duffel === "object") {
    const catalog = Array.isArray(offer?.availableExtras) ? offer.availableExtras : [];
    const byId = new Map(catalog.map((x) => [String(x.serviceId), x]));
    for (const [serviceId, sel] of Object.entries(duffel)) {
      if (!sel) continue;
      const e = byId.get(String(serviceId));
      if (!e) continue;
      const type = String(e.type || "").toLowerCase();
      if (type && type !== "baggage") continue;
      rows.push({
        status: BAGGAGE_STATUS.SELECTED_PAID,
        kind: mapBaggageKind(e.metadata?.baggageType || e.baggageType),
        quantity: 1,
        weightKg: finiteOrNull(e.metadata?.maximumWeightKg),
        currency: strOrNull(e.totalCurrency ?? e.currency),
        price: finiteOrNull(e.totalAmount ?? e.amount),
        passengerId: null,
        segmentIndex: null,
        label: strOrNull(e.name),
      });
    }
  }

  const supp = selections.supplementPaxSelections;
  if (supp && typeof supp === "object") {
    const unit =
      selections.supplementUnitFaresIls ||
      selections.supplementUnitFares ||
      {};
    for (const [key, val] of Object.entries(supp)) {
      if (!val || (typeof val === "object" && Object.keys(val).length === 0)) {
        continue;
      }
      const price = finiteOrNull(unit[key]);
      rows.push({
        status: BAGGAGE_STATUS.SELECTED_PAID,
        kind: "checked",
        quantity: 1,
        weightKg: null,
        currency: price != null ? "ILS" : null,
        price,
        passengerId: null,
        segmentIndex: null,
        label: String(key),
      });
    }
  }

  return rows;
}

function mapBaggageKind(raw) {
  const s = String(raw || "").toLowerCase();
  if (!s) return "unknown";
  if (s.includes("carry") || s.includes("cabin") || s.includes("trolley")) {
    return "carryOn";
  }
  if (s.includes("check") || s.includes("hold") || s.includes("luggage")) {
    return "checked";
  }
  return "unknown";
}

function finiteOrNull(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function strOrNull(v) {
  if (v == null) return null;
  const s = String(v).trim();
  return s || null;
}
